import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Car, Bike, Users, Trophy, User, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PlayerTeamModal({ userId, userName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('racing');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPlayerTeam();
  }, [userId]);

  const fetchPlayerTeam = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/player/${userId}/team`, { withCredentials: true });
      setData(response.data);
      // Auto-select tab based on which team exists
      if (!response.data.racing_team && response.data.moto_team) {
        setActiveTab('moto');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  const EntityCard = ({ entity, type, accentColor }) => {
    if (!entity) return null;
    const hasBoost = !!entity.boost;

    return (
      <div className={`bg-zinc-900 border ${hasBoost ? 'border-yellow-500/60' : 'border-zinc-800'} p-4 flex items-center space-x-4 relative`}>
        {hasBoost && (
          <div
            className="absolute -top-2 -left-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-yellow-500 text-black shadow-lg"
            title={entity.boost.name}
            data-testid={`boost-indicator-${type}`}
          >
            <Zap size={10} className="fill-black" />
            {entity.boost.name?.toUpperCase() || 'BOOST'}
          </div>
        )}
        {entity.image_url ? (
          <img src={entity.image_url} alt={entity.name} className="w-16 h-16 object-cover" />
        ) : (
          <div className={`w-16 h-16 bg-${accentColor}/20 flex items-center justify-center`}>
            <span className="font-heading font-bold text-xl text-zinc-500">
              {entity.name?.charAt(0) || '?'}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-bold truncate">{entity.name}</p>
          {entity.tuner && <p className="text-sm text-zinc-400">{entity.tuner}</p>}
          {entity.number && <p className="text-sm text-zinc-500">#{entity.number}</p>}
        </div>
        <div className="text-right">
          <p className={`font-heading font-bold text-xl text-${accentColor}`}>
            {entity.points || 0}
          </p>
          <p className="text-xs text-zinc-500">points</p>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div 
        className="bg-card border border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary flex items-center justify-center">
              <User size={24} className="text-white" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl">{userName}'s Team</h2>
              <p className="text-sm text-zinc-400">View team selection</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 transition-colors"
            data-testid="close-player-modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">{error}</p>
            </div>
          ) : (
            <>
              {/* League Tabs */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setActiveTab('racing')}
                  className={`flex items-center space-x-2 px-4 py-2 font-bold uppercase transition-all ${
                    activeTab === 'racing'
                      ? 'bg-primary text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <Car size={18} />
                  <span>Racing</span>
                  {data?.racing_points > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-white/20 text-xs">{data.racing_points} pts</span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('moto')}
                  className={`flex items-center space-x-2 px-4 py-2 font-bold uppercase transition-all ${
                    activeTab === 'moto'
                      ? 'bg-secondary text-black'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <Bike size={18} />
                  <span>Moto</span>
                  {data?.moto_points > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-black/20 text-xs">{data.moto_points} pts</span>
                  )}
                </button>
              </div>

              {/* Racing Team */}
              {activeTab === 'racing' && (
                <div className="space-y-6">
                  {data?.racing_team ? (
                    <>
                      {data.racing_team.team_name && (
                        <div className="bg-zinc-900 border border-primary/30 px-4 py-2 mb-2">
                          <span className="text-xs text-zinc-500 uppercase tracking-wider mr-2">Team:</span>
                          <span className="font-heading font-bold text-primary">{data.racing_team.team_name}</span>
                        </div>
                      )}
                      {/* Drivers */}
                      <div>
                        <h3 className="font-heading font-bold text-lg text-primary mb-3 flex items-center">
                          <Users size={18} className="mr-2" />
                          Drivers ({data.racing_team.drivers?.length || 0})
                        </h3>
                        <div className="space-y-2">
                          {data.racing_team.drivers?.map((driver, idx) => (
                            <EntityCard key={idx} entity={driver} type="driver" accentColor="primary" />
                          ))}
                          {(!data.racing_team.drivers || data.racing_team.drivers.length === 0) && (
                            <p className="text-zinc-500 italic">No drivers selected</p>
                          )}
                        </div>
                      </div>

                      {/* Cars */}
                      <div>
                        <h3 className="font-heading font-bold text-lg text-primary mb-3 flex items-center">
                          <Car size={18} className="mr-2" />
                          Cars ({data.racing_team.cars?.length || 0})
                        </h3>
                        <div className="space-y-2">
                          {data.racing_team.cars?.map((car, idx) => (
                            <EntityCard key={idx} entity={car} type="car" accentColor="primary" />
                          ))}
                          {(!data.racing_team.cars || data.racing_team.cars.length === 0) && (
                            <p className="text-zinc-500 italic">No cars selected</p>
                          )}
                        </div>
                      </div>

                      {/* Tuner */}
                      <div>
                        <h3 className="font-heading font-bold text-lg text-primary mb-3 flex items-center">
                          <Trophy size={18} className="mr-2" />
                          Tuner
                        </h3>
                        {data.racing_team.tuner ? (
                          <EntityCard entity={data.racing_team.tuner} type="tuner" accentColor="primary" />
                        ) : (
                          <p className="text-zinc-500 italic">No tuner selected</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <Car size={48} className="text-zinc-600 mx-auto mb-4" />
                      <p className="text-zinc-400">No racing team selected</p>
                    </div>
                  )}
                </div>
              )}

              {/* Moto Team */}
              {activeTab === 'moto' && (
                <div className="space-y-6">
                  {data?.moto_team ? (
                    <>
                      {data.moto_team.team_name && (
                        <div className="bg-zinc-900 border border-secondary/30 px-4 py-2 mb-2">
                          <span className="text-xs text-zinc-500 uppercase tracking-wider mr-2">Team:</span>
                          <span className="font-heading font-bold text-secondary">{data.moto_team.team_name}</span>
                        </div>
                      )}
                      {/* Riders */}
                      <div>
                        <h3 className="font-heading font-bold text-lg text-secondary mb-3 flex items-center">
                          <Users size={18} className="mr-2" />
                          Riders ({data.moto_team.riders?.length || 0})
                        </h3>
                        <div className="space-y-2">
                          {data.moto_team.riders?.map((rider, idx) => (
                            <EntityCard key={idx} entity={rider} type="rider" accentColor="secondary" />
                          ))}
                          {(!data.moto_team.riders || data.moto_team.riders.length === 0) && (
                            <p className="text-zinc-500 italic">No riders selected</p>
                          )}
                        </div>
                      </div>

                      {/* Bikes */}
                      <div>
                        <h3 className="font-heading font-bold text-lg text-secondary mb-3 flex items-center">
                          <Bike size={18} className="mr-2" />
                          Bikes ({data.moto_team.bikes?.length || 0})
                        </h3>
                        <div className="space-y-2">
                          {data.moto_team.bikes?.map((bike, idx) => (
                            <EntityCard key={idx} entity={bike} type="bike" accentColor="secondary" />
                          ))}
                          {(!data.moto_team.bikes || data.moto_team.bikes.length === 0) && (
                            <p className="text-zinc-500 italic">No bikes selected</p>
                          )}
                        </div>
                      </div>

                      {/* Moto Tuner */}
                      <div>
                        <h3 className="font-heading font-bold text-lg text-secondary mb-3 flex items-center">
                          <Trophy size={18} className="mr-2" />
                          Tuner
                        </h3>
                        {data.moto_team.tuner ? (
                          <EntityCard entity={data.moto_team.tuner} type="tuner" accentColor="secondary" />
                        ) : (
                          <p className="text-zinc-500 italic">No tuner selected</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <Bike size={48} className="text-zinc-600 mx-auto mb-4" />
                      <p className="text-zinc-400">No moto team selected</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
